import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { X, Check, ArrowUpRight, ShieldCheck } from "lucide-react";
import { scrollToId } from "@/components/Nav";

const fmt = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n);

const CAPOFAMIGLIA = 120;
const POLIZZA_BASE = 180;
const POLIZZA_ALTA = 300;
const SOGLIA_CANONE = 1250;

export default function Calculator() {
  const [rent, setRent] = useState(950);

  const { provvigione, deposito, tradOwner, tradTenant, polizza, danniMax, sfTotal, saving } = useMemo(() => {
    const prov = Math.round(rent * 1.22);
    const dep = rent * 2;
    const pol = rent <= SOGLIA_CANONE ? POLIZZA_BASE : POLIZZA_ALTA;
    const sf = CAPOFAMIGLIA + pol;
    return {
      provvigione: prov,
      deposito: dep,
      tradOwner: prov,
      tradTenant: prov + dep,
      polizza: pol,
      danniMax: rent <= SOGLIA_CANONE ? "2.500" : "5.000",
      sfTotal: sf,
      saving: prov * 2 + dep - sf,
    };
  }, [rent]);

  const fill = ((rent - 500) / (2500 - 500)) * 100;

  const tradRows = [
    { label: "Provvigione agenzia — proprietario", note: "un canone + IVA 22%", value: fmt(provvigione) },
    { label: "Provvigione agenzia — inquilino", note: "un canone + IVA 22%", value: fmt(provvigione) },
    { label: "Deposito cauzionale", note: "2 mensilità immobilizzate, spesso trattenute indebitamente", value: fmt(deposito) },
    { label: "Inquilino moroso", note: "nessuna copertura: canoni persi e spese legali a carico tuo", value: "—" },
    { label: "Stato dell'immobile", note: "danni difficili da contestare senza accertamento terzo", value: "—" },
  ];

  const sfRows = [
    { label: "Intermediazione", note: "zero provvigioni di agenzia, per entrambi", value: "0 €" },
    { label: "Polizza Capofamiglia", note: "a protezione di eventuali danni da conduzione · premio annuale, da rinnovare annualmente", value: fmt(CAPOFAMIGLIA) },
    { label: "Polizza sostitutiva del deposito", note: `copre danni fino a ${danniMax} € · vale fino a scadenza contratto, si rifà al rinnovo`, value: fmt(polizza) },
    { label: "Inquilino moroso", note: "fino a 3 mensilità di indennizzo + tutela legale incluse", value: "incluso" },
    { label: "Stato dell'immobile", note: "verbale di consegna e riconsegna: danni accertati da terzi", value: "incluso" },
  ];

  return (
    <section data-testid="calculator-section" className="bg-navy text-white py-14 lg:py-20 relative overflow-hidden">
      <div className="absolute -top-32 -right-32 w-[480px] h-[480px] rounded-full bg-azure/15 blur-3xl" />
      <div className="relative max-w-6xl mx-auto px-5 lg:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
          <div className="max-w-2xl">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6 }}
              className="eyebrow text-gold mb-4"
            >
              Il confronto
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7 }}
              className="font-serif font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.12]"
            >
              Affittare con noi <span className="italic text-azure">o senza?</span> Fanno la differenza i numeri.
            </motion.h2>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="w-full lg:max-w-sm shrink-0"
          >
            <div className="flex items-end justify-between mb-3 flex-wrap gap-3">
              <label htmlFor="rent-slider" className="text-sm text-white/60 uppercase tracking-[0.2em]">
                Canone mensile
              </label>
              <span className="font-serif font-bold text-3xl text-gold" data-testid="rent-monthly-display">
                {fmt(rent)}
              </span>
            </div>
            <input
              id="rent-slider"
              type="range"
              min={500}
              max={2500}
              step={50}
              value={rent}
              onChange={(e) => setRent(Number(e.target.value))}
              className="calc-slider"
              style={{ "--fill": `${fill}%` }}
              data-testid="rent-calculator-slider"
              aria-label="Canone mensile"
            />
            <div className="flex justify-between text-xs text-white/40 mt-2">
              <span>500 €</span>
              <span>2.500 €</span>
            </div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="mt-10"
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <div className="rounded-3xl border border-white/10 bg-ink/40 backdrop-blur-xl p-5 lg:p-7" data-testid="compare-traditional-card">
              <p className="eyebrow text-white/40 mb-1.5">Affitto tradizionale</p>
              <p className="font-serif font-bold text-xl mb-4 text-white/80">Cosa paghi (e cosa rischi)</p>
              <ul className="space-y-2.5">
                {tradRows.map((r, i) => (
                  <li key={i} className="flex items-start gap-3" data-testid={`compare-trad-row-${i}`}>
                    <span className="shrink-0 mt-0.5 inline-flex items-center justify-center w-6 h-6 rounded-full bg-red-500/15 text-red-400">
                      <X size={13} />
                    </span>
                    <div className="flex-1 flex items-start justify-between gap-4 border-b border-white/5 pb-2.5">
                      <div>
                        <p className="text-sm font-medium text-white/85 leading-snug">{r.label}</p>
                        <p className="text-[11px] text-white/40 mt-0.5">{r.note}</p>
                      </div>
                      <span className="font-serif font-bold text-base text-white/90 whitespace-nowrap">{r.value}</span>
                    </div>
                  </li>
                ))}
              </ul>
              <div className="mt-5 rounded-2xl bg-red-500/10 border border-red-500/20 p-4">
                <p className="text-[11px] text-white/50 uppercase tracking-wider mb-0.5">Esborso iniziale complessivo</p>
                <p className="font-serif font-bold text-2xl text-red-400" data-testid="compare-trad-total">{fmt(tradOwner + tradTenant)}</p>
                <p className="text-[11px] text-white/40 mt-0.5">proprietario {fmt(tradOwner)} + inquilino {fmt(tradTenant)}</p>
              </div>
            </div>

            <div className="relative rounded-3xl border-2 border-azure/60 bg-ink/40 backdrop-blur-xl p-5 lg:p-7" data-testid="compare-semplifica-card">
              <span className="absolute -top-4 left-7 inline-flex items-center gap-1.5 bg-gold text-ink text-xs font-bold uppercase tracking-wider rounded-full px-4 py-1.5">
                <ShieldCheck size={13} /> Conviene
              </span>
              <p className="eyebrow text-azure mb-1.5">Con Semplificaffitto.it</p>
              <p className="font-serif font-bold text-xl mb-4">Cosa paghi. Punto.</p>
              <ul className="space-y-2.5">
                {sfRows.map((r, i) => (
                  <li key={i} className="flex items-start gap-3" data-testid={`compare-sf-row-${i}`}>
                    <span className="shrink-0 mt-0.5 inline-flex items-center justify-center w-6 h-6 rounded-full bg-azure/15 text-azure">
                      <Check size={13} />
                    </span>
                    <div className="flex-1 flex items-start justify-between gap-4 border-b border-white/5 pb-2.5">
                      <div>
                        <p className="text-sm font-medium text-white/90 leading-snug">{r.label}</p>
                        <p className="text-[11px] text-white/40 mt-0.5">{r.note}</p>
                      </div>
                      <span className="font-serif font-bold text-base text-azure whitespace-nowrap">{r.value}</span>
                    </div>
                  </li>
                ))}
              </ul>
              <div className="mt-5 rounded-2xl bg-azure/10 border border-azure/30 p-4">
                <p className="text-[11px] text-white/50 uppercase tracking-wider mb-0.5">Esborso iniziale complessivo</p>
                <p className="font-serif font-bold text-2xl text-azure" data-testid="compare-sf-total">{fmt(sfTotal)}</p>
                <p className="text-[11px] text-white/40 mt-0.5">proprietario 0 € + inquilino {fmt(sfTotal)} · poi solo il 9% del canone al mese, a contratto attivo</p>
              </div>
            </div>
          </div>

          <div className="mt-5 rounded-2xl border border-gold/30 bg-gold/10 p-4 lg:p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4" data-testid="compare-saving-banner">
            <p className="text-sm lg:text-base">
              <span className="font-serif font-bold text-gold text-xl mr-2">{fmt(saving)}</span>
              <span className="text-white/80">di costi iniziali in meno rispetto all'affitto tradizionale — e il rischio morosità non è più tuo.</span>
            </p>
            <a
              href="#contatti"
              data-testid="calculator-cta-btn"
              onClick={(e) => {
                e.preventDefault();
                scrollToId("#contatti");
              }}
              className="group shrink-0 inline-flex items-center gap-2 bg-gold text-ink font-semibold rounded-full px-6 py-3.5 text-sm hover:bg-white transition-colors duration-300"
            >
              Richiedi la valutazione gratuita
              <ArrowUpRight size={18} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
          </div>

          <p className="text-[11px] text-white/40 mt-4">
            Simulazione indicativa: la valutazione reale dipende da zona, tipologia e stato dell'immobile.
            Il capofamiglia è un premio annuale; la polizza sostitutiva copre fino alla scadenza naturale del
            contratto e va riemessa a ogni rinnovo.
          </p>
        </motion.div>
      </div>
    </section>
  );
}
